import { VIDEO_HOSTS } from '../videoPlatforms';
import { meetbotService } from '../../services/meetbotService';

/**
 * Куда ведёт вставленная ссылка: скачиваем видео, зовём бота или не поняли.
 *
 * Один разбор на оба сценария — человек не обязан знать, что за ссылкой на
 * запись и за ссылкой на встречу стоят разные сервисы.
 */

// Площадки встреч — как в bit-meetbot/app/platforms/registry.py.
//
// 🔴 С 30.09.2026 площадки включает и выключает админка (настройка
// meetbot_platforms в bit-auth), и настоящий список экран берёт у самого бота:
// GET /meetbot-api/platforms, один раз за сессию (loadMeetingPlatforms).
// Ссылка на выключенную площадку получает обычное «не понимаю ссылку» — бот
// всё равно ответил бы на неё тем же.
//
// Списки ниже — ЗАПАСНЫЕ: ими экран живёт, пока ответ не пришёл или если бот
// не ответил. Это ровно то, что экран знал до выключателя, поэтому сбой
// ручки ничего не ломает.
//
// Правило сверки — своё у каждой площадки, форма та же, что у ответа
// GET /platforms: `suffix` — хост или его поддомен, `exact` — хост целиком.
// Толк живёт на поддомене пространства организации, поэтому сверяется
// СУФФИКСОМ. 🔴 А БИТ.Link и БИТ.LinkGo — ХОСТОМ ЦЕЛИКОМ, как и их адаптеры
// (bit-meetbot/app/platforms/bitlink.py, linkgo.py): за первым стоит
// BigBlueButton, за вторым Jitsi Meet — оба ставят и другие, и по форме
// ссылки чужой стенд от нашего не отличить.
// Разойтись этому списку с реестром адаптеров нельзя: экран отправит бота на
// ссылку, которую сервис не понимает, и человек получит отказ уже после того,
// как решил, что всё сработало.
//
// 🔴 Zoom в запасном списке НЕТ намеренно. Адаптер есть, но по умолчанию
// выключен и включается только админкой; экран узнаёт о нём из ответа бота
// (hosts ['zoom.us'], match 'suffix'). Сбой ручки не должен звать бота туда,
// куда админ его не пускал.
export const FALLBACK_MEETING_PLATFORMS = [
  { name: 'telemost', title: 'Яндекс Телемост', short: 'Телемост',
    hosts: ['telemost.yandex.ru', 'telemost.360.yandex.ru'], match: 'suffix' },
  { name: 'ktalk', title: 'Контур.Толк', short: 'Толк', hosts: ['ktalk.ru'], match: 'suffix' },
  { name: 'bitlink', title: 'БИТ.Link', short: 'БИТ.Link', hosts: ['link.1cbit.ru'], match: 'exact' },
  { name: 'linkgo', title: 'БИТ.LinkGo', short: 'LinkGo', hosts: ['linkgo.1cbit.ru'], match: 'exact' },
];

const hostsBy = (rule) => FALLBACK_MEETING_PLATFORMS
  .filter((p) => p.match === rule).flatMap((p) => p.hosts);
export const MEETING_HOSTS = hostsBy('suffix');
export const EXACT_MEETING_HOSTS = hostsBy('exact');

/** «А, Б, В и Г» — полные имена площадок для подписи. */
export function supportedPlatformsText(list = meetingPlatforms()) {
  const titles = list.map((p) => p.title);
  if (titles.length === 0) return '';
  if (titles.length === 1) return titles[0];
  return `${titles.slice(0, -1).join(', ')} и ${titles[titles.length - 1]}`;
}

/** Короткие имена через запятую — для плитки на телефоне. */
export function supportedPlatformsShort(list = meetingPlatforms()) {
  return list.map((p) => p.short).join(', ');
}

// Те же площадки словами — подписи запасного списка. Экраны берут живую
// подпись из useMeetingPlatforms; эти остаются для текста, которому ждать
// ответа бота некогда, и как образец в тестах.
export const SUPPORTED_PLATFORMS = supportedPlatformsText(FALLBACK_MEETING_PLATFORMS);
export const SUPPORTED_PLATFORMS_SHORT = supportedPlatformsShort(FALLBACK_MEETING_PLATFORMS);

// --- Список с сервера ---------------------------------------------------------

let serverPlatforms = null;   // нормализованный ответ /platforms или null — запасной
let pending = null;           // один запрос на сессию
let lastFailureAt = 0;        // когда запрос упал в последний раз; 0 — не падал
const listeners = new Set();

// После сбоя бота переспрашиваем не чаще раза в три минуты и в фоне: иначе
// каждое открытие экрана и каждая ссылка на видео снова ждали бы лежащий бот.
export const RETRY_AFTER_MS = 3 * 60 * 1000;
// Сколько ссылка на видео готова ждать ПЕРВЫЙ ответ бота. Дольше — разбор
// идёт по запасному списку: видео от бота не зависит и ждать его не должно.
export const FIRST_LOAD_WAIT_MS = 1500;

const MATCH_RULES = ['exact', 'suffix'];

/**
 * Ответ /platforms в проверенном виде; null — ответ не тот, живём по запасному.
 *
 * Пустой массив — это ответ, а не сбой: админ выключил все площадки, и
 * экран честно не зовёт бота никуда.
 */
function normalize(list) {
  if (!Array.isArray(list)) return null;
  return list
    .filter((p) => p && typeof p.name === 'string' && Array.isArray(p.hosts)
      && MATCH_RULES.includes(p.match))
    .map((p) => ({
      name: p.name,
      title: typeof p.title === 'string' && p.title ? p.title : p.name,
      short: typeof p.short === 'string' && p.short ? p.short : p.name,
      hosts: p.hosts.filter((h) => typeof h === 'string' && h).map((h) => h.toLowerCase()),
      match: p.match,
    }));
}

/** Площадки, по которым экран решает сейчас: с сервера или запасные. */
export function meetingPlatforms() {
  return serverPlatforms || FALLBACK_MEETING_PLATFORMS;
}

/** Подписка на приход списка с сервера; возвращает отписку. */
export function subscribeMeetingPlatforms(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * Спросить у бота включённые площадки — один раз за сессию.
 *
 * Никогда не отклоняется. Сбой оставляет запасной список; снова спросим не
 * раньше чем через RETRY_AFTER_MS (бот мог моргнуть на выкате). Удачный ответ
 * запоминается до перезагрузки страницы — правка в админке доезжает до экрана
 * со следующим входом.
 *
 * Args:
 *   fetchPlatforms: источник списка; по умолчанию GET /meetbot-api/platforms.
 */
export function loadMeetingPlatforms({ fetchPlatforms = () => meetbotService.getPlatforms() } = {}) {
  if (pending) return pending;
  if (serverPlatforms || (lastFailureAt && Date.now() - lastFailureAt < RETRY_AFTER_MS)) {
    return Promise.resolve();
  }
  pending = Promise.resolve()
    .then(fetchPlatforms)
    .then((platforms) => {
      const list = normalize(platforms);
      if (!list) throw new Error('неожиданный ответ /platforms');
      serverPlatforms = list;
      lastFailureAt = 0;
      listeners.forEach((fn) => fn(list));
    })
    .catch(() => { lastFailureAt = Date.now(); })
    .finally(() => { pending = null; });
  return pending;
}

/**
 * Дождаться списка площадок перед разбором ссылки — но недолго.
 *
 * 🔴 Ждём только ПЕРВЫЙ запрос и не дольше FIRST_LOAD_WAIT_MS. Если бот уже
 * однажды не ответил, не ждём вовсе: разбор идёт по запасному списку, а
 * повтор (не чаще RETRY_AFTER_MS) уходит в фон. До правки по ревью 30.09
 * каждая ссылка на YouTube при лежащем боте ждала пятисекундный таймаут.
 */
export async function awaitMeetingPlatforms({ timeoutMs = FIRST_LOAD_WAIT_MS, ...opts } = {}) {
  const failedBefore = lastFailureAt > 0;
  const load = loadMeetingPlatforms(opts);
  if (serverPlatforms || failedBefore) return;
  let timer;
  await Promise.race([load, new Promise((resolve) => { timer = setTimeout(resolve, timeoutMs); })]);
  clearTimeout(timer);
}

/** Забыть ответ сервера (тесты). */
export function resetMeetingPlatforms() {
  serverPlatforms = null;
  pending = null;
  lastFailureAt = 0;
  listeners.clear();
}

const URL_IN_TEXT = /https?:\/\/[^\s<>"'«»]+/gi;
const BARE_HOST = /^[a-z0-9-]+(\.[a-z0-9-]+)+(:\d+)?(\/\S*)?$/i;
// Хвост ссылки из текста: пунктуация предложения и всё с первого не-ASCII
// символа («…», «—», кириллица) — в ссылках площадок встреч их нет.
const trimTail = (u) => u.replace(/[^\x21-\x7e][\s\S]*$/, '').replace(/[.,;:!?)\]}]+$/, '');

/**
 * Ссылка из того, что человек вставил: приглашение целиком («Подключиться:
 * https://…») или адрес без схемы (`telemost.yandex.ru/j/…`). Тот же разбор,
 * что у бота (bit-meetbot/app/main.py:_meeting_url): разойдутся — экран
 * отвергнет ссылку, которую бот принял бы, или наоборот.
 * Одна ссылка без текста — как есть. Две разные ссылки в тексте — не
 * выбираем: бот ушёл бы по старой из переписки «старая …, новая …».
 */
export function extractLink(text) {
  const value = String(text || '').trim();
  const all = value.match(URL_IN_TEXT) || [];
  if (all.length === 1 && all[0] === value) return value;
  const found = new Set(all.map(trimTail));
  if (found.size === 1) return [...found][0];
  if (found.size === 0 && BARE_HOST.test(value)) return `https://${value}`;
  return value;
}

function hostOf(url) {
  try {
    return (new URL(extractLink(url)).hostname || '').toLowerCase();
  } catch {
    // Не URL вовсе или порт не число — для нас это просто «не узнали».
    return '';
  }
}

function matches(host, list) {
  // 🔴 Только точное совпадение или поддомен. Подстрока пустила бы
  // evil-ktalk.ru и ktalk.ru.evil.com, а на такую ссылку бот пошёл бы гостем.
  return list.some((h) => host === h || host.endsWith(`.${h}`));
}

function isMeetingHost(host, platform) {
  return platform.match === 'exact' ? platform.hosts.includes(host) : matches(host, platform.hosts);
}

export function routeLink(url) {
  const host = hostOf(url);
  if (!host) return { kind: 'unknown', host: '' };
  if (meetingPlatforms().some((p) => isMeetingHost(host, p))) return { kind: 'meeting', host };
  if (matches(host, VIDEO_HOSTS)) return { kind: 'video', host };
  // Хост возвращаем и здесь: по нему считается спрос на новые площадки.
  return { kind: 'unknown', host };
}
